/**
 * Customer branding context (DEV-182): logo and text of the login screen and
 * the page title of every page.
 *
 * Loaded once from the public `GET /api/branding` (the login screen needs it
 * before anyone is signed in). A failed load keeps the AssetRadar defaults —
 * branding must never stand between a user and the login. The System page
 * hands back what it saved, so the title changes at once.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { fetchBranding } from './api';
import { pageTitle } from './brandingModel';
import type { Branding } from './types';

export interface BrandingContextValue {
  /** null until loaded, and when loading failed. */
  branding: Branding | null;
  setBranding: (next: Branding) => void;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }): ReactNode {
  const [branding, setBrandingState] = useState<Branding | null>(null);

  useEffect(() => {
    let active = true;
    fetchBranding()
      .then((next) => { if (active) setBrandingState(next); })
      .catch(() => { /* keep the AssetRadar defaults */ });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    document.title = pageTitle(branding?.title_suffix);
  }, [branding]);

  const setBranding = useCallback((next: Branding): void => setBrandingState(next), []);
  const value = useMemo<BrandingContextValue>(() => ({ branding, setBranding }), [branding, setBranding]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

/** Access the branding context. Throws when used outside `BrandingProvider`. */
export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (ctx === null) {
    throw new Error('useBranding() muss innerhalb von <BrandingProvider> verwendet werden.');
  }
  return ctx;
}
